import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography } from '@mui/material';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const NkChart = ({ familyCases }) => {
  const { t } = useTranslation('global');
  const [darkMode, setDarkMode] = useState(false);
  const [chartLabels, setChartLabels] = useState([]);
  const [chartValues, setChartValues] = useState([]);
  const rawData = familyCases.cases || [];

  useEffect(() => {
    const checkDarkMode = () => {
      setDarkMode(document.body.classList.contains('dark-mode'));
    };

    checkDarkMode();
    const validate = new MutationObserver(checkDarkMode);
    validate.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => validate.disconnect();
  }, []);

  useEffect(() => {
    if (!rawData || rawData.length > 0) {
      const labels = rawData.map(item => item.caseResult?.[0]?.caseName || 'Sin nombre');
      const values = rawData.map(item => {
        const layers = item.caseResult?.[0]?.layers || [];
        const level1 = layers.find(layer => layer.level === 1);
        return level1 ? level1.flexibility : 0;
      });

      setChartLabels(labels);
      setChartValues(values);
    }
  }, [familyCases]);

  if (Object.keys(familyCases.cases).length === 0) {
    return (
      <Paper
        sx={{
          width: '100%',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #551a8b',
          borderRadius: '20px',
          boxShadow: '7px 7px 20px rgba(0, 0, 0, 0.5)',
          marginBottom: '50px'
        }}
      >
        <Typography variant="h6" color="textSecondary">
          {t('graphs.titleNAN')}
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
          {t('graphs.subtitle')}
        </Typography>
      </Paper>

    );
  }

  const infoColors = darkMode ? {
    flexibility: 'rgb(179, 136, 255)',
    pmax: 'rgb(255, 138, 138)',
  } : {
    flexibility: 'rgb(128, 0, 128)',
    pmax: 'rgb(255, 99, 132)',
  };

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: t("board.casesHistory.n-kFlexibility"),
        data: chartValues,
        fill: false,
        borderColor: infoColors.flexibility,
        tension: 0.3,
        pointBackgroundColor: '#ff6a13',
        pointStyle: 'circle',
        pointRadius: 5,
      },
      {
        label: t("board.casesHistory.n-kMaxFlexibility"),
        data: chartLabels.map(() => 100),
        borderColor: infoColors.pmax,
        backgroundColor: darkMode ? 'rgba(179, 136, 255, 0.2)' : 'rgba(128, 0, 128, 0.2)',
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
      }
    ],
  };

  const chartStyle = {
    width: '100%',
    backgroundColor: darkMode ? '#121212' : '#f5f5f5',
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12
          },
          color: darkMode ? '#e0e0e0' : '#666'
        }
      },
      title: {
        display: true,
        text: t("board.casesHistory.flexibility"),
        font: {
          size: 16
        },
        color: darkMode ? '#e0e0e0' : '#333'
      },
    },
    scales: {
      y: {
        min: 0,
        max: 110,
        ticks: {
          stepSize: 10,
          color: darkMode ? '#e0e0e0' : '#666'
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: t("board.casesHistory.flexibility"),
          font: {
            weight: 'bold',
          },
          color: darkMode ? '#e0e0e0' : '#333',
        },
      },
      x: {
        title: {
          display: true,
          text: t("board.casesHistory.cases"),
          font: {
            weight: 'bold',
          },
          color: darkMode ? '#e0e0e0' : '#333',
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: darkMode ? '#e0e0e0' : '#666',
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
        },
      },
    },
  };

  const containerStyles = {
    padding: '15px',
    gap: '15px',
    border: darkMode ? '2px solid #bb86fc' : '2px solid #800080',
    borderRadius: '10px',
    backgroundColor: darkMode ? '#1e1e1e' : '#f9f9f9',
  };

  const buttonStyles = {
    margin: '5px',
    backgroundColor: darkMode ? '#bb86fc' : '#800080',
    color: '#fff'
  };

  const sortByName = () => {
    const combined = chartLabels.map((label, i) => ({ label, value: chartValues[i] }));
    combined.sort((a, b) => a.label.localeCompare(b.label));
    setChartLabels(combined.map(item => item.label));
    setChartValues(combined.map(item => item.value));
  };

  const sortByValue = () => {
    const combined = chartLabels.map((label, i) => ({ label, value: chartValues[i] }));
    combined.sort((a, b) => a.value - b.value);
    setChartLabels(combined.map(item => item.label));
    setChartValues(combined.map(item => item.value));
  };

  return (
    <>
      <Box style={containerStyles}>
        <Line data={chartData} options={options} style={chartStyle} />
      </Box>
      <Box>
        <Button style={buttonStyles} onClick={sortByName}>{t('graphs.OrderByname')}</Button>
        <Button style={buttonStyles} onClick={sortByValue}>{t('graphs.OrderByValue')}</Button>
      </Box>

    </>
  );

};

export default NkChart;
